import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useMe } from "../api/queries.ts";
import { everLoggedInThisSession } from "./session.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  路由登录守卫
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「给平台管理后台增加路由登录守卫。未登录时跳转登录页」。
 *
 * 以前未登录打开 `/admin`，看到的是一个**空壳后台**：左栏是空的、
 * 中间一句「请先登录」加一个链接。那看着像后台坏了，而且他还得自己点一下。
 *
 * 🔴 **必须等 `me` 查完再判。** `useMe()` 刚挂载时 `data` 是 undefined 但
 *    `isPending` 是 true —— 这时候就跳的话，**每个已登录的人硬刷新后台都会
 *    被弹去登录页**（cookie 明明是好的，只是那一瞬间还没查回来）。
 *
 * 🔴 **`replace: true`。** 不 replace 的话历史里留着 `/admin` 那一条，
 *    用户在登录页按返回 → 回到 /admin → 又被弹去登录页，来回跳出不去。
 *
 * ⚠️ 把**当前完整地址**带过去（`/admin?tab=review` 这种），登录完回到原处。
 *    只带 pathname 的话，他点的是「提现审核」的链接，登完回来落在概览页。
 *
 * ⚠️ 这一条只管**登录没登录**，不管**有没有后台权限** ——
 *    已登录但不是后台账号的人再弹去登录页只会来回打转（他登录状态是好的）。
 *    那种情况在 `/admin` 里单独给一屏说明。
 *
 * ⚠️ 前端守卫只是**体验**，不是安全边界。真正的闸门在服务端
 *    `requireAdmin(权限键)` 上 —— 前端拦住的人，接口照样能被直接调。
 */
export function useRequireLogin(): { ready: boolean; loggedIn: boolean } {
  const { data: me, isPending } = useMe();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPending || me) return;
    /**
     * 🔴 **登录过之后掉线的，不要踢走。**（老板：过期该在顶部提示，
     *    给「停留此页 / 重新登录」两个选择。）
     *    他正用着，页面上可能有填了一半的东西 —— 跳走等于把那些吃掉。
     *    那种情况由 `SessionExpiredBar` 接管，这里什么都不做。
     *
     * ⚠️ **直接打开这一页时（从没登录过）仍然跳**：那没什么可"停留"的，
     *    整页本来就是空的，让他对着一个空后台猜更糟。
     */
    if (everLoggedInThisSession()) return;
    const here = `${window.location.pathname}${window.location.search}`;
    void navigate({ to: "/login", search: { redirect: here }, replace: true });
  }, [isPending, me, navigate]);

  return { ready: !isPending, loggedIn: Boolean(me) };
}
